/**
 * useGuidedHints Hook
 * 
 * Manages the guided hints system:
 * - Requests hints from API after AI responses (when audio stops for >1s)
 * - Verifies if user followed the hint
 * - Simple rule: show hint if no hint is active, let API decide everything else
 * - Tracks hint history
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useSessionServices, type ConversationLog } from '../context/SessionServicesContext';
import { EmotionType } from '../types/emotion';
import { getLocalizedCharacterName } from '../utils/characterName';
import { logger } from '../utils/logger';

// Hard ceiling on guided-hint API calls per session (defense-in-depth; the
// server enforces its own authoritative cap). Sits far above any real session.
const MAX_HINT_API_CALLS_PER_SESSION = 80;

interface Message {
  speaker: 'user' | 'ai';
  text: string;
  timestamp: number;
}

interface Hint {
  id: string; // Unique ID for state tracking
  text: string;
  timestamp: number;
  type: 'suggestion' | 'warning' | 'praise';
  status: 'pending' | 'verified' | 'dismissed'; // State tracking
  exchangeIndex: number; // Exchange where hint was given
  givenAt: number; // Exchange index when hint was given
  expiresAt?: number; // Exchange index when hint expires (default: givenAt + 5)
  verifiedAt?: number; // Exchange index when hint was verified
  followed?: boolean; // Legacy: keep for backwards compatibility
  autoResolved?: boolean; // Hint was resolved without being opened
}

interface UseGuidedHintsParams {
  enabled: boolean; // Not used directly - guidedModeEnabled is synced from sessionStorage
  maxHints: number;
  sessionStatus: string;
  aiTextResponseComplete?: boolean; // Track when AI text response is complete (triggers hint request)
  isPaused?: boolean; // Track if session is manually paused
  isAnalysisMode?: boolean; // Track if session is in Analysis Mode (Westworld-style debugging)
  isUserSpeaking?: boolean; // Not used - kept for backwards compatibility
  aiResponseInProgress?: string; // Not used - kept for backwards compatibility
  messages: Message[];
  sessionData: {
    scenario: {
      id: string;
      title?: string;
      description?: string;
      objectives?: string[];
      category?: string;
      industry?: string;
      evaluation?: {
        criteria?: Array<{ name: string; description?: string; enabled?: boolean }>;
      };
      successCriteria?: any;
      [key: string]: any;
    };
  } | null;
  sessionStartTime: Date | null;
  sessionDuration: number;
  userId?: string;
  emotionHistory: Array<{ timestamp: number; emotion: EmotionType }>;
  credentials?: any; // Authentication credentials for API calls
}

interface UseGuidedHintsReturn {
  hints: Hint[];
  hintsShown: number; // Only counts ACTIONABLE hints (warning/suggestion), NOT praise (encouragement)
  warningsShown: number; // Count of warning-type hints (always shown)
  suggestionsShown: number; // Count of suggestion/praise-type hints that were opened
  hintsFollowed: number; // NEW: Count of hints that were actually followed by user
  guidedModeEnabled: boolean;
  setGuidedModeEnabled: (enabled: boolean) => void;
  activeHint: Hint | null; // Current hint being displayed (requires pause)
  pendingHint: Hint | null; // Hint waiting in help button (not yet opened)
  successHint: string | null; // NEW: Hint that was successfully followed (triggers success toast)
  dismissActiveHint: () => void; // User clicked continue or timer expired
  dismissSuccessHint: () => void; // NEW: Dismiss success toast
  consumePendingHint: () => void; // User clicked help button to see pending hint
  isHintWaitingToShow: boolean; // User requested hint but waiting for avatar to finish
  debugState: {
    isAudioPlaying: boolean;
    aiResponseInProgress: boolean;
    isUserSpeaking: boolean;
    isPaused: boolean;
    isAvatarBusy: boolean;
    bothSilentAndSafe: boolean;
    shownAt?: number; // Timestamp when hint was shown (for post-mortem)
  } | null; // Debug state for UI display
}


export function useGuidedHints({
  enabled: _enabled, // Not used - guidedModeEnabled synced from sessionStorage
  maxHints,
  sessionStatus,
  aiTextResponseComplete,
  isPaused = false,
  isAnalysisMode = false, // Disable hints during Analysis Mode
  isUserSpeaking: _isUserSpeaking = false, // Not used - hints show immediately
  aiResponseInProgress: _aiResponseInProgress = '', // Not used - hints show immediately
  messages,
  sessionData,
  sessionStartTime,
  sessionDuration,
  userId,
  emotionHistory,
  credentials = null // Authentication credentials for API calls
}: UseGuidedHintsParams): UseGuidedHintsReturn {
  const { requestGuidedHint, verifyHintFollowed, getCurrentLanguage } = useSessionServices();

  // 🧪 DEBUG MODE: Force hints to appear after every AI response (for testing)
  // Enable by setting localStorage.guidedHintsDebugMode = 'true'
  const [debugMode, setDebugMode] = useState(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem('guidedHintsDebugMode') === 'true';
  });
  
  // Listen for debug mode changes
  useEffect(() => {
    const checkDebugMode = () => {
      if (typeof window === 'undefined') return;
      const enabled = localStorage.getItem('guidedHintsDebugMode') === 'true';
      setDebugMode(enabled);
      if (enabled) {
        logger.debug('[GuidedHints] 🧪 DEBUG MODE ENABLED - Hints will appear after every AI response');
      }
    };
    
    window.addEventListener('storage', checkDebugMode);
    return () => window.removeEventListener('storage', checkDebugMode);
  }, []);
  
  // Read guided mode preference from sessionStorage
  const guidedModeEnabledFromStorage = (() => {
    if (typeof window === 'undefined') {
      return false;
    }
    const storedValue = sessionStorage.getItem('guidedModeEnabled');
    // Only log once on initial mount
    if (storedValue === null) {
      return true;
    }
    return storedValue === 'true';
  })();
  const [guidedModeEnabled, setGuidedModeEnabled] = useState(guidedModeEnabledFromStorage);

  // Log initial state on mount
  useEffect(() => {
    logger.debug('[GuidedHints] 🚀 Hook initialized:', {
      guidedModeEnabled,
      sessionStatus,
      messagesCount: messages.length,
      aiTextResponseComplete
    });
  }, []); // Only on mount

  const [hints, setHints] = useState<Hint[]>([]);
  const [hintsShown, setHintsShown] = useState<number>(0);
  const hintsShownRef = useRef<number>(0); // Track hintsShown in ref to avoid recreating callback
  const [hintsFollowed, setHintsFollowed] = useState<number>(0);
  const [lastHintTimestamp, setLastHintTimestamp] = useState<number>(0);
  const [activeHint, setActiveHint] = useState<Hint | null>(null);
  const activeHintRef = useRef<Hint | null>(null); // Track activeHint in ref for useEffect checks
  const [pendingHint, setPendingHint] = useState<Hint | null>(null);
  const [successHint, setSuccessHint] = useState<string | null>(null);
  const lastHintRef = useRef<string | null>(null);
  const lastHintTypeRef = useRef<'suggestion' | 'warning' | 'praise' | null>(null); // CRITICAL: Track hint type for verification
  // const lastAiResponseCompleteRef = useRef<boolean>(false); // DEPRECATED: No longer used - hints now trigger after user messages
  const pendingHintTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isRequestingHintRef = useRef<boolean>(false); // CRITICAL: Prevent concurrent API requests
  // Defense-in-depth cost guardrail: the maxHints cap below only counts hints
  // actually SHOWN, so when the API keeps replying "no hint needed" the count
  // never rises and we'd call the (paid) endpoint on every single user message.
  // This counts API CALLS regardless of outcome and hard-stops a runaway session.
  // The server enforces its own authoritative cap; this just avoids wasted calls.
  const hintApiCallsRef = useRef<number>(0);
  const lastProcessedUserMessageIndexRef = useRef<number>(-1); // Track last processed user message index
  const lastVerifiedMessageIndexRef = useRef<number>(-1); // Track last message index that was verified against a hint
  const lastHintMessageIndexRef = useRef<number>(-1); // Track index of user message that triggered the last hint
  
  // Migrate old hints to new format (add IDs and states if missing)
  useEffect(() => {
    setHints(prev => prev.map(h => {
      if (!h.id) {
        // Old hint without ID - migrate it
        return {
          ...h,
          id: `hint_legacy_${h.timestamp}_${Math.random().toString(36).substr(2, 9)}`,
          status: h.status || (h.followed ? 'verified' : 'pending'),
          exchangeIndex: h.exchangeIndex || 0,
          givenAt: h.givenAt || 0,
          expiresAt: h.expiresAt || (h.givenAt || 0) + 5
        };
      }
      return h;
    }));
  }, []);
  
  // Keep ref in sync with state
  useEffect(() => {
    activeHintRef.current = activeHint;
  }, [activeHint]);
  
  // Track last analyzed exchange index for progressive analysis
  const [lastAnalyzedExchangeIndex, setLastAnalyzedExchangeIndex] = useState<number>(-1);

  useEffect(() => {
    // Only log when we have new hints
    if (hints.length > 0 && hints[hints.length - 1].timestamp > Date.now() - 1000) {
      logger.debug('[GuidedHints] New hint added:', {
        hintType: hints[hints.length - 1].type,
        hintLength: hints[hints.length - 1].text.length
      });
    }
  }, [hints]);

  // Sync guided mode state with sessionStorage changes
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'guidedModeEnabled') {
        setGuidedModeEnabled(e.newValue === 'true');
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  /**
   * Effect: Clear hints when entering Analysis Mode
   * This prevents pre-generated or pending hints from appearing while debugging
   */
  useEffect(() => {
    if (isAnalysisMode) {
      logger.debug('[GuidedHints] 🧹 Analysis Mode enabled: Clearing active and pending hints');
      setActiveHint(null);
      setPendingHint(null);
      if (pendingHintTimeoutRef.current) {
        clearTimeout(pendingHintTimeoutRef.current);
        pendingHintTimeoutRef.current = null;
      }
      lastHintRef.current = null;
    }
  }, [isAnalysisMode]);

  /**
   * Effect: Verify hints whenever user speaks
   * 
   * LOGIC:
   * - When user sends a message AND there's an active hint → verify
   * - BUT only verify if this message is AFTER the message that generated the hint
   * - Don't verify the same message twice
   */
  useEffect(() => {
    // Get the last user message
    const lastMessage = messages[messages.length - 1];
    if (!lastMessage || lastMessage.speaker !== 'user') {
      return; // Last message is not from user
    }

    const lastMessageIndex = messages.length - 1;
    
    // Skip if we already verified this message
    if (lastMessageIndex <= lastVerifiedMessageIndexRef.current) {
      return;
    }

    // Skip if there's no active hint
    if (!lastHintRef.current || lastHintRef.current.trim().length === 0) {
      return;
    }

    // CRITICAL: Check if the active hint is already verified
    // Don't verify hints that are already verified
    const activeHintObj = hints.find(h => h.text === lastHintRef.current);
    if (activeHintObj && activeHintObj.status === 'verified') {
      logger.debug('[GuidedHints] ⏭️ Skipping verification - hint already verified');
      lastVerifiedMessageIndexRef.current = lastMessageIndex;
      return;
    }

    // CRITICAL: Don't verify the message that generated the hint!
    // Only verify messages AFTER the hint was generated
    if (lastHintMessageIndexRef.current >= 0 && lastMessageIndex <= lastHintMessageIndexRef.current) {
      return;
    }

    // Don't verify PRAISE hints - they're encouragement, not instructions to follow
    if (lastHintTypeRef.current === 'praise') {
      lastVerifiedMessageIndexRef.current = lastMessageIndex;
      return;
    }

    // Validate message text
    if (!lastMessage.text || lastMessage.text.trim().length === 0) {
      return;
    }

    // Mark this message as verified
    lastVerifiedMessageIndexRef.current = lastMessageIndex;

    // Verify if user followed the hint
    const verifyFollowed = async () => {
      try {
        // Get pending and verified hints for context
        const pendingHints = hints.filter(h => h.status === 'pending' && h.type !== 'praise').map(h => ({
          id: h.id,
          hint: h.text,
          type: h.type,
          exchangeIndex: h.exchangeIndex
        }));
        const verifiedHints = hints.filter(h => h.status === 'verified').map(h => ({
          id: h.id,
          hint: h.text,
          type: h.type
        }));
        
        const result = await verifyHintFollowed(
          lastHintRef.current!, 
          lastMessage.text,
          pendingHints,
          verifiedHints,
          credentials
        );

        // Only process if we got a verifiedHintId AND it's not already verified
        if (result.verifiedHintId) {
          const verifiedHintId = result.verifiedHintId;
          
          // Check if hint is already verified (prevent double-counting)
          const hintToVerify = hints.find(h => h.id === verifiedHintId);
          if (hintToVerify && hintToVerify.status === 'verified') {
            logger.debug('[GuidedHints] ⏭️ Hint already verified, skipping update');
            return;
          }
          
          // Update hint status to verified
          setHints(prev => {
            const updated = [...prev];
            const hintIndex = updated.findIndex(h => h.id === verifiedHintId);
            
            if (hintIndex !== -1 && updated[hintIndex].status !== 'verified') {
              const currentExchangeIndex = messages.filter(m => m.speaker === 'user').length - 1;
              updated[hintIndex] = { 
                ...updated[hintIndex], 
                status: 'verified',
                verifiedAt: currentExchangeIndex,
                followed: true // Legacy compatibility
              };
              
              // Only increment counter if we actually changed status
              setHintsFollowed(prev => prev + 1);
              
              // Show success toast
              setSuccessHint(updated[hintIndex].text);
              
              // CRITICAL: Clear lastHintRef if this was the active hint OR if there are no more pending hints
              // This prevents re-verification when multiple hints are pending and a different one is verified
              const verifiedHintText = updated[hintIndex].text;
              if (lastHintRef.current === verifiedHintText) {
                logger.debug('[GuidedHints] 🔒 Clearing lastHintRef - hint verified');
                lastHintRef.current = null;
              } else {
                // If a different hint was verified, check if the hint in lastHintRef is still pending
                // If it's now verified or doesn't exist, clear lastHintRef
                const pendingHintsAfterVerify = updated.filter(h => h.status === 'pending' && h.type !== 'praise');
                const lastHintStillPending = pendingHintsAfterVerify.some(h => h.text === lastHintRef.current);
                if (!lastHintStillPending && lastHintRef.current) {
                  logger.debug('[GuidedHints] 🔒 Clearing lastHintRef - no more pending hints match');
                  lastHintRef.current = null;
                }
              }
            }
            return updated;
          });
        } else if (result.followed && !result.verifiedHintId) {
          // Legacy support: if followed=true but no verifiedHintId, update last hint
          const hintToVerify = hints.find(h => h.timestamp === lastHintTimestamp);
          if (hintToVerify && hintToVerify.status !== 'verified') {
            setHints(prev => {
              const updated = [...prev];
              const hintIndex = updated.findIndex(h => h.timestamp === lastHintTimestamp);
              if (hintIndex !== -1 && updated[hintIndex].status !== 'verified') {
                const currentExchangeIndex = messages.filter(m => m.speaker === 'user').length - 1;
                updated[hintIndex] = { 
                  ...updated[hintIndex], 
                  status: 'verified',
                  verifiedAt: currentExchangeIndex,
                  followed: true
                };
                setHintsFollowed(prev => prev + 1);
                setSuccessHint(updated[hintIndex].text);
                
                // CRITICAL: Clear lastHintRef if this was the active hint
                if (lastHintRef.current === updated[hintIndex].text) {
                  logger.debug('[GuidedHints] 🔒 Clearing lastHintRef - hint verified (legacy)');
                  lastHintRef.current = null;
                }
              }
              return updated;
            });
          }
        }
      } catch (error) {
        console.error('[GuidedHints] Failed to verify hint:', error);
      }
    };

    verifyFollowed();
  }, [messages, lastHintTimestamp, hints, credentials]); // Include credentials to avoid stale auth in verification

  /**
   * NEW Effect: Proactive verification of PENDING hints (not yet opened)
   * If user responds while hint is pending, check if they resolved it on their own
   */
  useEffect(() => {
    if (!pendingHint) return;

    // CRITICAL: Check if pending hint is already verified (prevent re-verification)
    const pendingHintInState = hints.find(h => h.id === pendingHint.id);
    if (pendingHintInState && pendingHintInState.status === 'verified') {
      logger.debug('[GuidedHints] ⏭️ Skipping pending verification - hint already verified');
      setPendingHint(null);
      return;
    }

    // Check if there's a new user message after pending hint was created
    const userMessagesAfterPending = messages.filter(
      msg => msg.speaker === 'user' && msg.timestamp > pendingHint.timestamp
    );

    if (userMessagesAfterPending.length > 0) {
      // Don't verify PRAISE hints - they're encouragement, not instructions to follow
      if (pendingHint.type === 'praise') {
        logger.debug('[GuidedHints] ℹ️ Skipping pending verification - praise hints are not actionable');
        return;
      }

      const lastUserMessage = userMessagesAfterPending[userMessagesAfterPending.length - 1];
      
      // CRITICAL: Validate message text before calling API
      if (!lastUserMessage.text || lastUserMessage.text.trim().length === 0) {
        logger.debug('[GuidedHints] ⏭️ Skipping pending verification - user message has no text');
        return;
      }
      
      if (!pendingHint.text || pendingHint.text.trim().length === 0) {
        logger.debug('[GuidedHints] ⏭️ Skipping pending verification - hint text is empty');
        return;
      }
      
      const verifyPending = async () => {
        try {
          logger.debug('[GuidedHints] 🔍 Proactively verifying pending hint...');
          
          // Get pending and verified hints for context
          const pendingHints = hints.filter(h => h.status === 'pending' && h.type !== 'praise').map(h => ({
            id: h.id,
            hint: h.text,
            type: h.type,
            exchangeIndex: h.exchangeIndex
          }));
          const verifiedHints = hints.filter(h => h.status === 'verified').map(h => ({
            id: h.id,
            hint: h.text,
            type: h.type
          }));
          
          const result = await verifyHintFollowed(
            pendingHint.text, 
            lastUserMessage.text,
            pendingHints,
            verifiedHints,
            credentials
          );

          // Only process if we got a verifiedHintId AND it's not already verified
          if (result.verifiedHintId) {
            const verifiedHintId = result.verifiedHintId;
            
            // Check if hint is already verified (prevent double-counting)
            const hintToVerify = hints.find(h => h.id === verifiedHintId);
            if (hintToVerify && hintToVerify.status === 'verified') {
              logger.debug('[GuidedHints] ⏭️ Pending hint already verified, skipping update');
              return;
            }
            
            logger.debug('[GuidedHints] ✅ Pending hint auto-resolved');
            
            // Update hint status to verified
            setHints(prev => {
              const updated = [...prev];
              const hintIndex = updated.findIndex(h => h.id === verifiedHintId);
              if (hintIndex !== -1 && updated[hintIndex].status !== 'verified') {
                const currentExchangeIndex = messages.filter(m => m.speaker === 'user').length - 1;
                updated[hintIndex] = { 
                  ...updated[hintIndex], 
                  status: 'verified',
                  verifiedAt: currentExchangeIndex,
                  followed: true // Legacy compatibility
                };
                
                // Only increment counter if we actually changed status
                setHintsFollowed(prev => prev + 1);
                
                // Clear pending hint and timeout
                if (pendingHintTimeoutRef.current) {
                  clearTimeout(pendingHintTimeoutRef.current);
                  pendingHintTimeoutRef.current = null;
                }
                setPendingHint(null);
                
                // Show success toast
                setSuccessHint(updated[hintIndex].text);
                
                // CRITICAL: Clear lastHintRef if this was the active hint OR if there are no more pending hints
                const verifiedHintText = updated[hintIndex].text;
                if (lastHintRef.current === verifiedHintText) {
                  logger.debug('[GuidedHints] 🔒 Clearing lastHintRef - pending hint verified');
                  lastHintRef.current = null;
                } else {
                  // If a different hint was verified, check if the hint in lastHintRef is still pending
                  const pendingHintsAfterVerify = updated.filter(h => h.status === 'pending' && h.type !== 'praise');
                  const lastHintStillPending = pendingHintsAfterVerify.some(h => h.text === lastHintRef.current);
                  if (!lastHintStillPending && lastHintRef.current) {
                    logger.debug('[GuidedHints] 🔒 Clearing lastHintRef - no more pending hints match');
                    lastHintRef.current = null;
                  }
                }
                
                // Trigger vibration feedback for success
                if ('vibrate' in navigator) {
                  logger.debug('[GuidedHints] 📳 Triggering success vibration for auto-resolved hint');
                  navigator.vibrate([50, 50, 50]); // Triple short pulse for success
                }
                
                logger.debug('[GuidedHints] ✅ Pending hint marked as verified');
              }
              return updated;
            });
          } else if (result.followed && !result.verifiedHintId) {
            // Legacy support: if followed=true but no verifiedHintId, update pending hint
            const hintToVerify = hints.find(h => h.id === pendingHint.id);
            if (hintToVerify && hintToVerify.status !== 'verified') {
              setHints(prev => {
                const updated = [...prev];
                const hintIndex = updated.findIndex(h => h.id === pendingHint.id);
                if (hintIndex !== -1 && updated[hintIndex].status !== 'verified') {
                  const currentExchangeIndex = messages.filter(m => m.speaker === 'user').length - 1;
                  updated[hintIndex] = { 
                    ...updated[hintIndex], 
                    status: 'verified',
                    verifiedAt: currentExchangeIndex,
                    followed: true
                  };
                  setHintsFollowed(prev => prev + 1);
                  setPendingHint(null);
                  setSuccessHint(updated[hintIndex].text);
                  
                  // CRITICAL: Clear lastHintRef if this was the active hint
                  if (lastHintRef.current === updated[hintIndex].text) {
                  logger.debug('[GuidedHints] 🔒 Clearing lastHintRef - pending hint verified (legacy)');
                    lastHintRef.current = null;
                  }
                }
                return updated;
              });
            }
          }
        } catch (error) {
          console.error('[GuidedHints] ❌ Failed to verify pending hint:', error);
        }
      };

      verifyPending();
    }
  }, [messages, pendingHint, hints, credentials]); // Include credentials to avoid stale auth in verification



  /**
   * Function to request a hint from the API
   * Called when: last message is AI + audio stopped for >1s + no active hint
   */
  const requestHintFromAPI = useCallback(async () => {
    logger.debug('[GuidedHints] 🎯 Requesting hint from API...');

    // CRITICAL: Check if we're already requesting a hint (prevent race conditions)
    if (isRequestingHintRef.current) {
      logger.debug('[GuidedHints] 🔴 BLOCKED: Already requesting a hint (concurrent request prevented)');
      return;
    }

    // Basic checks
    if (!guidedModeEnabled) {
      logger.debug('[GuidedHints] 🔴 BLOCKED: Guided mode disabled');
      return;
    }

    if (Number.isFinite(maxHints) && hintsShownRef.current >= maxHints) {
      logger.debug('[GuidedHints] 🔴 BLOCKED: Max hints reached', {
        maxHints,
        hintsShown: hintsShownRef.current
      });
      return;
    }

    // Defense-in-depth: hard-stop a runaway session even if the API never shows
    // a hint (in which case hintsShown stays 0 and the cap above never trips).
    if (hintApiCallsRef.current >= MAX_HINT_API_CALLS_PER_SESSION) {
      logger.debug('[GuidedHints] 🔴 BLOCKED: Session hint API call cap reached', {
        calls: hintApiCallsRef.current
      });
      return;
    }

    if (sessionStatus !== 'active') {
      logger.debug('[GuidedHints] 🔴 BLOCKED: Session not active');
      return;
    }

    if (isPaused) {
      logger.debug('[GuidedHints] 🔴 BLOCKED: Session paused');
      return;
    }

    if (isAnalysisMode) {
      logger.debug('[GuidedHints] 🔴 BLOCKED: Analysis Mode active (hints disabled during debugging)');
      return;
    }

    if (!sessionData) {
      logger.debug('[GuidedHints] 🔴 BLOCKED: No session data');
      return;
    }

    // Check if there's already a hint active (don't request if one is showing)
    // NOTE: We allow requests even if pendingHint exists - API will decide if hint should show
    if (activeHintRef.current) {
      logger.debug('[GuidedHints] 🔴 BLOCKED: Hint already active');
      return;
    }

    // Check if user has spoken at least once (skip initial greeting)
    const userMessages = messages.filter(msg => msg.speaker === 'user');
    if (userMessages.length === 0) {
      logger.debug('[GuidedHints] 🔴 BLOCKED: User has not spoken yet (skipping first avatar greeting)');
      return;
    }

    logger.debug('[GuidedHints] ✅ All checks passed - requesting hint (always after each avatar response)');
    
    // Set the flag to prevent concurrent requests
    isRequestingHintRef.current = true;

    try {
      // 🧪 DEBUG MODE: Generate artificial hints instead of calling API
      if (debugMode) {
        logger.debug('[GuidedHints] 🧪 DEBUG MODE: Generating artificial hint');
        
        const hintTypes: Array<'suggestion' | 'warning' | 'praise'> = ['suggestion', 'warning', 'praise'];
        const hintType = hintTypes[hintsShownRef.current % hintTypes.length];
        
        const debugHints = {
          suggestion: [
            'Try asking more open-ended questions',
            'Consider acknowledging their concerns first',
            'Active listening helps build rapport'
          ],
          warning: [
            'Avoid dismissive language',
            'Your tone seems confrontational',
            'Let them finish speaking'
          ],
          praise: [
            'Great job showing empathy!',
            'Excellent active listening!',
            'Well done building rapport'
          ]
        };
        
        const hintText = debugHints[hintType][Math.floor(Math.random() * debugHints[hintType].length)];
        const currentExchangeIndex = messages.filter(m => m.speaker === 'user').length - 1;
        const hintId = `hint_debug_${Date.now()}`;
        const newHint: Hint = { 
          id: hintId,
          text: `🧪 [DEBUG] ${hintText}`, 
          timestamp: Date.now(), 
          type: hintType,
          status: 'pending',
          exchangeIndex: currentExchangeIndex,
          givenAt: currentExchangeIndex,
          expiresAt: currentExchangeIndex + 5
        };
        
        setActiveHint(newHint);
        // Add ALL hints to hints array for verification and history tracking
        // Filtering for display (only warnings in bubbles) happens in TranscriptOverlay
        setHints(prev => [...prev, newHint]);
        // Only count actionable hints (warning/suggestion), not praise (encouragement)
        if (newHint.type !== 'praise') {
          setHintsShown(prev => {
            const newValue = prev + 1;
            hintsShownRef.current = newValue; // Keep ref in sync
            return newValue;
          });
        }
        setLastHintTimestamp(newHint.timestamp);
        // Save the index of the user message that generated this hint
        // Find the last user message index (loop backwards)
        let lastUserMessageIndex = -1;
        for (let i = messages.length - 1; i >= 0; i--) {
          if (messages[i].speaker === 'user') {
            lastUserMessageIndex = i;
            break;
          }
        }
        lastHintMessageIndexRef.current = lastUserMessageIndex;
        lastHintRef.current = newHint.text;
        lastHintTypeRef.current = newHint.type; // Track type for verification
        
        if ('vibrate' in navigator) {
          navigator.vibrate(50);
        }
        
        return;
      }
      
      // Build conversation log (FULL conversation for complete context)
      const conversationLog: ConversationLog = {
        exchanges: messages.map(msg => ({
          speaker: msg.speaker === 'user' ? 'trainee' : 'avatar',
          message: msg.text,
          timestamp: new Date(msg.timestamp).toISOString()
        })),
        metadata: {
          scenarioId: sessionData?.scenario?.id || 'unknown',
          startTime: new Date(sessionStartTime || Date.now()).toISOString(),
          endTime: new Date().toISOString(),
          userId: userId || 'unknown'
        },
        sessionDuration,
        emotionHistory
      };

      const language = getCurrentLanguage().split('-')[0];
      const characterNameRaw = (sessionData.scenario as any).characterName;
      const characterName = characterNameRaw ? getLocalizedCharacterName(characterNameRaw, getCurrentLanguage()) : undefined;
      
      // Extract scenario data to send to backend (avoid Firestore queries)
      const scenarioDataToSend = {
        title: sessionData.scenario.title,
        description: sessionData.scenario.description,
        objectives: sessionData.scenario.objectives,
        category: sessionData.scenario.category,
        industry: (sessionData.scenario as any).industry,
        evaluation: (sessionData.scenario as any).evaluation,
        successCriteria: (sessionData.scenario as any).successCriteria
      };
      
      // Build previousHints array with states
      // Filter hints by status and include state information
      const currentExchangeIndex = conversationLog.exchanges.length - 1;
      
      // Auto-expire hints that are past their expiry
      const hintsWithExpiry = hints.map(h => {
        if (h.status === 'pending' && h.expiresAt && currentExchangeIndex > h.expiresAt) {
          return { ...h, status: 'dismissed' as const };
        }
        return h;
      });
      
      // Update hints state if any expired
      const expiredHints = hintsWithExpiry.filter(h => h.status === 'dismissed' && hints.find(oh => oh.id === h.id)?.status !== 'dismissed');
      if (expiredHints.length > 0) {
        setHints(hintsWithExpiry);
      }
      
      const previousHintsToSend = hintsWithExpiry.map((hint) => {
        // Find the exchange index where hint was given
        let hintExchangeIndex = hint.givenAt || 0;
        if (!hint.givenAt) {
          // Fallback: find user message before hint timestamp
          for (let i = conversationLog.exchanges.length - 1; i >= 0; i--) {
            const exchange = conversationLog.exchanges[i];
            const exchangeTimestamp = exchange.timestamp ? new Date(exchange.timestamp).getTime() : 0;
            if (exchange.speaker === 'trainee' && exchangeTimestamp <= hint.timestamp) {
              hintExchangeIndex = i;
              break;
            }
          }
        }
        
        return {
          id: hint.id,
          exchangeIndex: hintExchangeIndex,
          type: hint.type,
          hint: hint.text,
          reason: '', // Not stored in frontend
          shouldShowHint: true, // All hints in array were shown
          status: hint.status,
          givenAt: hint.givenAt || hintExchangeIndex,
          expiresAt: hint.expiresAt,
          verifiedAt: hint.verifiedAt
        };
      });
      
      hintApiCallsRef.current += 1; // Count every real API call (cost guardrail)
      const result = await requestGuidedHint(
        sessionData.scenario.id,
        null,
        conversationLog,
        emotionHistory,
        hintsShownRef.current, // Use ref value to avoid dependency on hintsShown state
        language,
        characterName,
        lastAnalyzedExchangeIndex >= 0 ? lastAnalyzedExchangeIndex : undefined,
        scenarioDataToSend,
        previousHintsToSend,
        credentials
      );

      logger.debug('[GuidedHints] API Response:', {
        shouldShowHint: result.shouldShowHint,
        type: (result as any).type,
        hasReason: Boolean(result.reason)
      });

      // Show hint if API decided
      if (result.shouldShowHint && result.hint) {
        const hintType = (result as any).type || 'suggestion';
        const currentExchangeIndex = conversationLog.exchanges.length - 1;
        
        // Generate unique ID for hint
        const hintId = `hint_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        
        const newHint: Hint = { 
          id: hintId,
          text: result.hint, 
          timestamp: Date.now(), 
          type: hintType as 'suggestion' | 'warning' | 'praise',
          status: 'pending',
          exchangeIndex: currentExchangeIndex,
          givenAt: currentExchangeIndex,
          expiresAt: currentExchangeIndex + 5 // Expire after 5 exchanges
        };
        
        logger.debug('[GuidedHints] ✅ Showing hint:', hintType);
        setActiveHint(newHint);
        // Add ALL hints to hints array for verification and history tracking
        // Filtering for display (only warnings in bubbles) happens in TranscriptOverlay
        setHints(prev => [...prev, newHint]);
        // Only count actionable hints (warning/suggestion), not praise (encouragement)
        if (newHint.type !== 'praise') {
          setHintsShown(prev => {
            const newValue = prev + 1;
            hintsShownRef.current = newValue; // Keep ref in sync
            return newValue;
          });
        }
        setLastHintTimestamp(newHint.timestamp);
        // Save the index of the user message that generated this hint
        // Find the last user message index (loop backwards)
        let lastUserMessageIndex = -1;
        for (let i = messages.length - 1; i >= 0; i--) {
          if (messages[i].speaker === 'user') {
            lastUserMessageIndex = i;
            break;
          }
        }
        lastHintMessageIndexRef.current = lastUserMessageIndex;
        lastHintRef.current = newHint.text;
        lastHintTypeRef.current = newHint.type; // Track type for verification
        
        if ('vibrate' in navigator) {
          navigator.vibrate(50);
        }
        
        setLastAnalyzedExchangeIndex(conversationLog.exchanges.length - 1);
      } else {
        // API said no hint needed
        logger.debug('[GuidedHints] ℹ️ No hint needed');
        setLastAnalyzedExchangeIndex(conversationLog.exchanges.length - 1);
      }

    } catch (error) {
      console.error('[GuidedHints] ❌ Failed to request hint:', error);
    } finally {
      // Always clear the flag when done (success or error)
      isRequestingHintRef.current = false;
      logger.debug('[GuidedHints] ✅ Request completed - flag cleared');
    }
  }, [messages, sessionData, sessionStartTime, sessionDuration, userId, emotionHistory, guidedModeEnabled, maxHints, sessionStatus, isPaused, isAnalysisMode, debugMode, lastAnalyzedExchangeIndex, credentials]);
  // NOTE: Removed 'hintsShown' from dependencies to prevent useEffect from re-triggering when hint is added
  // 'hintsShown' is only used in the API call to send current count, but we can use the ref/state value directly

  /**
   * Request hints when USER message transcript is RECEIVED (not when user starts speaking)
   * - Triggers when a new user message appears in messages array WITH VALID TEXT
   * - Changed from "after avatar response" to "after user response"
   * - This allows hints to evaluate the user's action before avatar responds
   * - CRITICAL: Only triggers when message.text is valid (transcript received, not just started speaking)
   */
  useEffect(() => {
    // Skip if no messages yet
    if (messages.length === 0) {
      return;
    }

    const lastMessage = messages[messages.length - 1];
    
    // Only trigger for user messages
    if (lastMessage?.speaker !== 'user') {
      return;
    }

    // CRITICAL: Only trigger if message has valid text (transcript received)
    // This prevents triggering when user starts speaking but transcript hasn't arrived yet
    if (!lastMessage.text || lastMessage.text.trim().length === 0) {
      logger.debug('[GuidedHints] ⏭️ Skipping - user message has no text yet (transcript not received)');
      return;
    }

    // Check if this is a new user message (not already processed)
    const lastUserMessageIndex = messages.length - 1;
    
    if (lastProcessedUserMessageIndexRef.current >= lastUserMessageIndex) {
      return; // Already processed this message
    }

    lastProcessedUserMessageIndexRef.current = lastUserMessageIndex;

    logger.debug('[GuidedHints] ✅ User message sent - requesting hint (NEW TIMING: after user, not avatar)', {
      sessionStatus,
      guidedModeEnabled,
      isPaused,
      messagesCount: messages.length,
      lastMessageSpeaker: lastMessage.speaker,
      activeHint: !!activeHintRef.current,
      pendingHint: !!pendingHint
    });
    
    // Basic checks before requesting
    if (sessionStatus !== 'active' || !guidedModeEnabled || isPaused || isAnalysisMode) {
      logger.debug('[GuidedHints] ⏭️ Skipping hint request - session not ready:', {
        sessionStatus,
        guidedModeEnabled,
        isPaused,
        isAnalysisMode
      });
      return;
    }

    // Request hint after user message
    requestHintFromAPI();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, sessionStatus, guidedModeEnabled, isPaused, isAnalysisMode, requestHintFromAPI]);

  /**
   * OLD: Request hints when AI response text is complete
   * DEPRECATED: Now requests happen after user messages instead
   * Keeping commented for reference
   */
  /*
  useEffect(() => {
    // Only trigger when aiTextResponseComplete changes from false to true (transition)
    const wasComplete = lastAiResponseCompleteRef.current;
    const isNowComplete = aiTextResponseComplete === true;
    
    // Update ref for next comparison
    lastAiResponseCompleteRef.current = isNowComplete;

    // Only proceed if transitioning from incomplete to complete
    if (!wasComplete && isNowComplete) {
      console.log('[GuidedHints] ✅ AI text response complete - requesting hint (same timing as emotion analysis)', {
        sessionStatus,
        guidedModeEnabled,
        isPaused,
        messagesCount: messages.length,
        lastMessageSpeaker: messages[messages.length - 1]?.speaker,
        activeHint: !!activeHintRef.current,
        pendingHint: !!pendingHint
      });
      
      // Basic checks before requesting
      if (sessionStatus !== 'active' || !guidedModeEnabled || isPaused) {
        console.log('[GuidedHints] ⏭️ Skipping hint request - session not ready:', {
          sessionStatus,
          guidedModeEnabled,
          isPaused
        });
        return;
      }

      // Check if last message is from AI
      const lastMessage = messages[messages.length - 1];
      if (lastMessage?.speaker === 'ai') {
        console.log('[GuidedHints] ✅ Last message is AI - requesting hint immediately');
        requestHintFromAPI();
      } else {
        console.log('[GuidedHints] ℹ️ Last message is not AI - skipping hint check');
      }
    } else if (wasComplete && !isNowComplete) {
      console.log('[GuidedHints] ℹ️ AI text response reset to incomplete (normal - preparing for next response)');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiTextResponseComplete, sessionStatus, guidedModeEnabled, isPaused, messages, requestHintFromAPI]);
  */

  // Dismiss active hint (user clicked continue or timer expired)
  const dismissActiveHint = () => {
    logger.debug('[GuidedHints] Dismissing active hint');
    // Warnings are already added to hints array when created, so no need to add again here
    // This prevents duplication. Suggestions/praise were never added, so they just disappear.
    setActiveHint(null);
  };

  // NEW: Dismiss success hint toast
  const dismissSuccessHint = () => {
    logger.debug('[GuidedHints] Dismissing success hint toast');
    setSuccessHint(null);
  };

  // NEW: Consume pending hint (user clicked help button to see it)
  const consumePendingHint = () => {
    if (!pendingHint) {
      console.warn('[GuidedHints] consumePendingHint called but no pending hint');
      return;
    }
    
    logger.debug('[GuidedHints] 💡 User requested pending hint - showing immediately');
    
    // Clear the 10s timeout
    if (pendingHintTimeoutRef.current) {
      clearTimeout(pendingHintTimeoutRef.current);
      pendingHintTimeoutRef.current = null;
    }
    
    // Show hint immediately
    setActiveHint(pendingHint);
    setPendingHint(null);
    
    // Add ALL hints to hints array for verification and history tracking
    // Filtering for display (only warnings in bubbles) happens in TranscriptOverlay
    setHints(prev => [...prev, pendingHint]);
    
    // NOW count it (user actually saw it) - only count actionable hints, not praise
    if (pendingHint.type !== 'praise') {
      setHintsShown(prev => {
        const newValue = prev + 1;
        hintsShownRef.current = newValue; // Keep ref in sync
        return newValue;
      });
    }
    setLastHintTimestamp(pendingHint.timestamp);
    // Save the index of the user message that generated this hint
    // Find the last user message index (loop backwards)
    let lastUserMessageIndex = -1;
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].speaker === 'user') {
        lastUserMessageIndex = i;
        break;
      }
    }
    lastHintMessageIndexRef.current = lastUserMessageIndex;
    lastHintRef.current = pendingHint.text;
    lastHintTypeRef.current = pendingHint.type; // Track type for verification
    
    logger.debug('[GuidedHints] ✅ Hint opened and counted');
  };

  // Calculate warning and suggestion counts
  // Note: Only hints that were actually opened are in the hints list
  const warningsShown = hints.filter(h => h.type === 'warning').length;
  const suggestionsShown = hints.filter(h => (h.type === 'suggestion' || h.type === 'praise') && !h.autoResolved).length;

  return {
    hints,
    hintsShown,
    warningsShown,
    suggestionsShown,
    hintsFollowed, // NEW: Number of hints successfully followed
    guidedModeEnabled,
    setGuidedModeEnabled,
    activeHint,
    pendingHint,
    successHint, // NEW: Hint that was successfully followed
    dismissActiveHint,
    dismissSuccessHint, // NEW: Dismiss success toast
    consumePendingHint,
    isHintWaitingToShow: false, // Always false - hints show immediately
    debugState: null as UseGuidedHintsReturn['debugState'] // Always null - debug state removed
  };
}

